import {
    Avatar,
    Box,
    Button,
    Chip,
    ClickAwayListener,
    Collapse,
    createStyles,
    Divider,
    Drawer,
    Grow,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    makeStyles,
    MenuList,
    Paper,
    Popper,
    Theme,
    Typography,
} from '@material-ui/core';
import React from 'react';
import PersonIcon from '@material-ui/icons/Person';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { mainTheme } from '../../Theme';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import clsx from 'clsx';
import { UserFeature, UserFeatureEnum } from '../../types/UserFeature';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
import LockIcon from '@material-ui/icons/Lock';
import HelpIcon from '@material-ui/icons/Help';
import StorageIcon from '@material-ui/icons/Storage';
import SettingsIcon from '@material-ui/icons/Settings';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import InfoIcon from '@material-ui/icons/Info';
import ImportContactsIcon from '@material-ui/icons/ImportContacts';
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary';
import UpdateIcon from '@material-ui/icons/Update';
import { useQuery } from 'react-apollo';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { FirmDetailsConfigData, FirmDetailsConfigQuery } from '../../firmSettingConfiguration/tabs/FirmDetailsTab';
import { SkeletonLoader } from '../loading/SkeletonLoader';
import { UserDefinedDialog } from '../../helpConfiguration/dialog/UserDefinedDialog';
import { ScrubEnquiryDataDialog } from '../../enquirySummary/dialogs/ScrubEnquiryDataDialog';
import { ChangePassword } from '../ChangePassword';
import CloseIcon from '@material-ui/icons/Close';
import { DocumentListFile } from '../../common/constants';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
        },
        avatar: {
            backgroundColor: '#fff',
            border: `1px solid ${mainTheme.TemplateColor.Primary}`,
            color: mainTheme.TemplateColor.Primary,
            fontSize: '0.85rem',
            fontWeight: 600,
            height: '36px',
            width: '36px',
        },
        userIcon: {
            fontSize: '1.35rem',
            color: mainTheme.TemplateColor.Primary,
        },
        paper: {
            marginRight: theme.spacing(2),
        },
        triggerPill: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '4px 4px 4px 4px',
            borderRadius: '24px',
            border: '1px solid transparent',
            cursor: 'pointer',
            transition: 'box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease, background-color 0.15s ease',
            '&:hover': {
                backgroundColor: mainTheme.CommonColor.LightBlue2,
                borderColor: mainTheme.CommonColor.LightBlue1,
                boxShadow: '0 4px 18px rgba(0,0,0,0.10)',
                transform: 'translateY(-1px)',
            },
            '@media (prefers-reduced-motion: reduce)': {
                '&:hover': {
                    transform: 'none',
                },
            },
        },
        triggerPillOpen: {
            backgroundColor: mainTheme.CommonColor.LightBlue2,
            borderColor: mainTheme.CommonColor.LightBlue1,
            boxShadow: '0 4px 18px rgba(0,0,0,0.10)',
        },
        button: {
            textTransform: 'none',
            fontSize: '15px',
            fontWeight: 600,
            // Explicit request: match the same text colour the other header buttons
            // (Self Help, Get Support, icon buttons) already use.
            color: mainTheme.TemplateColor.Primary,
            minWidth: 'auto',
            padding: '2px 4px',
            borderRadius: '999px',
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
        arrowIcon: {
            fontSize: '20px',
            color: mainTheme.TextColor.Tertiary,
            transition: 'transform 0.2s ease, color 0.15s ease',
        },
        arrowIconOpen: {
            color: mainTheme.TemplateColor.Primary,
            transform: 'rotate(180deg)',
        },
        menuPaper: {
            marginTop: theme.spacing(1),
            minWidth: '300px',
            maxWidth: '320px',
            borderRadius: '12px',
            border: `1px solid ${mainTheme.CommonColor.LightGrey2}`,
            boxShadow: '0 10px 40px rgba(35,37,59,0.16)',
            overflow: 'hidden',
        },
        menuHeader: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.5),
            padding: theme.spacing(2),
        },
        headerAvatar: {
            backgroundColor: '#fff',
            border: `1px solid ${mainTheme.TemplateColor.Primary}`,
            color: mainTheme.TemplateColor.Primary,
            fontSize: '1rem',
            fontWeight: 700,
            height: '44px',
            width: '44px',
        },
        headerText: {
            minWidth: 0,
        },
        headerName: {
            fontSize: '15px',
            fontWeight: 700,
            color: mainTheme.TextColor.Primary,
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        firmChip: {
            marginTop: theme.spacing(0.5),
            height: '20px',
            fontSize: '11px',
            fontWeight: 600,
            color: mainTheme.TextColor.Tertiary,
            borderColor: mainTheme.CommonColor.LightGrey2,
        },
        // Sits next to firmChip (a plain sibling - Chip's own default inline-flex display
        // already lets them sit side by side and wrap if the pill runs out of room). Self
        // contained (not composed with firmChip) so its accent colour isn't at the mercy of
        // JSS rule injection order - explicit request for "some colour, not too bright": the
        // same soft bg-tint + accent border/text pairing every other badge in this app uses
        // (--rv-accent-blue/-bg from App.css), not a solid filled chip.
        sourceSystemChip: {
            marginTop: theme.spacing(0.5),
            marginLeft: theme.spacing(0.5),
            height: '20px',
            fontSize: '11px',
            fontWeight: 600,
            color: 'var(--rv-accent-blue)',
            backgroundColor: 'var(--rv-accent-blue-bg)',
            borderColor: 'var(--rv-accent-blue)',
        },
        // Firm name - explicit request to show it alongside the existing Firm ID chip, same
        // treatment as headerName one size down (12px vs 15px) so it reads as a subtitle.
        headerFirmName: {
            fontSize: '12px',
            fontWeight: 500,
            color: mainTheme.TextColor.Tertiary,
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        sectionLabel: {
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: mainTheme.TextColor.Tertiary,
            lineHeight: '1.4',
            padding: theme.spacing(1.5, 2, 0.5),
        },
        menuDivider: {
            margin: theme.spacing(0.5, 0),
        },
        iconChip: {
            width: '28px',
            height: '28px',
            borderRadius: '8px',
            backgroundColor: mainTheme.CommonColor.LightBlue2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        },
        listItemModern: {
            borderRadius: '8px',
            margin: '2px 8px',
            width: 'auto',
            transition: 'background-color 0.15s ease',
            '&:hover': {
                backgroundColor: mainTheme.CommonColor.LightBlue2,
            },
        },
        logoutItem: {
            '&:hover': {
                backgroundColor: 'rgba(213,0,0,0.08)',
            },
        },
        nested: {
            paddingLeft: theme.spacing(4),
        },
        listItem: {
            color: mainTheme.TemplateColor.Primary,
        },
        listItemIcon: {
            minWidth: '40px',
        },
        icon: {
            color: mainTheme.TemplateColor.Primary,
            fontSize: '16px',
        },
        changePasswordDrawer: {
            minWidth: '450px',
        },
        drawerHeader: {
            display: 'flex',
            justifyContent: 'flex-end',
        },
    }),
);

export enum ResourceEnum {
    Acuity = 'acuityResourceCenter',
    CRM = 'crmResourceCenter',
    Tasks = 'taskResourceCenter',
    Matters = 'mattersResourceCenter',
    Timesheet = 'timesheetResourceCenter',
}

type UserCardV2Props = {
    tenantId?: string;
    isForceToChangePass?: boolean;
    showResetLeapAccess?: boolean;
    showResetSmokeballAccess?: boolean;
    showResetActionStepAccess?: boolean;
    showResetAffinityAccess?: boolean;
    sourceSystem?: string;
};

type UserCardV2State = {
    open: boolean;
    isHelpOpen: boolean;
    isScrubEnquiryOpen: boolean;
    isChangePasswordOpen: boolean;
};

export const UserCardV2 = (props: UserCardV2Props) => {
    const classes = useStyles();

    const currentUser = useCurrentUser('cache-first');

    // Explicit request: show the firm's configured name (FirmDetailsTab.tsx's own settings)
    // next to the Firm ID chip, in place of tenant.companyName. Reuses that tab's own query/
    // types rather than duplicating them - cache-first per explicit request, same policy the
    // tenant query above (AppHeader.tsx's TenantData) already uses.
    const firmDetailsQuery = useQuery<FirmDetailsConfigData>(FirmDetailsConfigQuery, { fetchPolicy: 'cache-first' });
    const firmName = firmDetailsQuery.data?.settings?.systems?.firmDetails?.config?.firmName;

    const [state, setState] = React.useState<UserCardV2State>({
        open: false,
        isHelpOpen: false,
        isScrubEnquiryOpen: false,
        isChangePasswordOpen: false,
    });

    const [open, setOpen] = React.useState(false);

    const anchorRef = React.useRef<HTMLButtonElement>(null);

    const [resourceOpen, setResourceOpen] = React.useState(false);

    const handleResourceOpen = () => {
        setResourceOpen(!resourceOpen);
    };

    const handleToggle = () => {
        setOpen((currentOpen) => !currentOpen);
    };

    const handleClose = (event: React.MouseEvent<EventTarget>) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        setOpen(false);
    };

    function handleListKeyDown(event: React.KeyboardEvent) {
        if (event.key === 'Tab') {
            event.preventDefault();
            setOpen(false);
        }
    }

    // return focus to the button when we transitioned from !open -> open
    const prevOpen = React.useRef(open);

    React.useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current!.focus();
        }
        prevOpen.current = open;
        // tslint:disable-next-line: align
    }, [open]);

    const handleLogout = (event: React.MouseEvent<EventTarget>) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        window.location.href = `/${props.tenantId}/logout`;

        setOpen(false);
    };

    const handleChangePassword = (event: React.MouseEvent<EventTarget>) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        setState((prevState) => {
            return {
                ...prevState,
                isChangePasswordOpen: true,
            };
        });

        setOpen(false);
    };

    const handleServiceLevels = (event: React.MouseEvent<EventTarget>) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        window.open(DocumentListFile.ServiceLevel, '_blank');

        setOpen(false);
    };

    const handleTrainingWebinars = (event: React.MouseEvent<EventTarget>) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }
        window.open('https://www.redraincorp.com/training-webinars', '_blank');

        setOpen(false);
    };

    const handleTrainingUpdates = (event: React.MouseEvent<EventTarget>) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }
        window.open('https://www.redraincorp.com/updates-and-tips', '_blank');

        setOpen(false);
    };

    const handleResetLeapAccess = (event: React.MouseEvent<EventTarget>) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        window.location.href = '/LeapAccessResetSignIn';

        setOpen(false);
    };

    const handleResetSmokeballAccess = (event: React.MouseEvent<EventTarget>) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        window.location.href = '/SmokeballAccessResetSignIn';

        setOpen(false);
    };

    const handleResetActionStepAccess = (event: React.MouseEvent<EventTarget>) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        window.location.href = '/ActionStepAccessResetSignIn';

        setOpen(false);
    };

    const handleResetAffinityAccess = (event: React.MouseEvent<EventTarget>) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        window.location.href = '/AffinityAccessResetSignIn';

        setOpen(false);
    };

    const handleReportCatalog = (event: React.MouseEvent<EventTarget>) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        window.location.href = '/reportsCatalog';

        setOpen(false);
    };

    const handleHelp = (event: React.MouseEvent<EventTarget>) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        setState((prevState) => {
            return {
                ...prevState,
                isHelpOpen: true,
            };
        });

        setOpen(false);
    };

    const handleScrubData = (event: React.MouseEvent<EventTarget>) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        setState((prevState) => {
            return {
                ...prevState,
                isScrubEnquiryOpen: true,
            };
        });

        setOpen(false);
    };

    const onCloseChangePassword = () => {
        setState((prevState) => {
            return {
                ...prevState,
                isChangePasswordOpen: false,
            };
        });
    };

    const onCloseHelp = () => {
        setState((prevState) => {
            return {
                ...prevState,
                isHelpOpen: false,
            };
        });
    };

    const onCloseScrub = () => {
        setState((prevState) => {
            return {
                ...prevState,
                isScrubEnquiryOpen: false,
            };
        });
    };

    const handleResource = (resource: ResourceEnum) => (event: React.MouseEvent<EventTarget>) => {
        window.location.href = `/${resource}`;
    };

    const userName = currentUser.data?.appUser?.name;
    const userInitials = userName
        ?.trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('');

    if (currentUser.loading) {
        return (
            <Box className={classes.root}>
                <SkeletonLoader variant="shape" shape="circle" width={36} height={36} />
                <SkeletonLoader variant="shape" shape="rect" width={110} height={32} />
            </Box>
        );
    }

    return (
        <>
            {state.isHelpOpen && <UserDefinedDialog open={state.isHelpOpen} onClose={onCloseHelp} />}
            {state.isScrubEnquiryOpen && <ScrubEnquiryDataDialog open={state.isScrubEnquiryOpen} onClose={onCloseScrub} />}
            {state.isChangePasswordOpen && (
                <Drawer
                    classes={{
                        paper: classes.changePasswordDrawer,
                    }}
                    anchor="right"
                    open={state.isChangePasswordOpen}
                    onClose={onCloseChangePassword}
                >
                    <div className={classes.drawerHeader}>
                        <IconButton aria-label="close" onClick={onCloseChangePassword}>
                            <CloseIcon />
                        </IconButton>
                    </div>
                    <ChangePassword />
                </Drawer>
            )}
            <Box className={classes.root}>
                <Box className={clsx(classes.triggerPill, open && classes.triggerPillOpen)}>
                    <Avatar className={classes.avatar}>
                        {userInitials || <PersonIcon className={classes.userIcon} />}
                    </Avatar>
                    <Button
                        ref={anchorRef}
                        size="small"
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        onClick={handleToggle}
                        endIcon={<ArrowDropDownIcon className={clsx(classes.arrowIcon, open && classes.arrowIconOpen)} />}
                        className={classes.button}
                    >
                        {currentUser.data?.appUser?.name}
                    </Button>
                </Box>
                <Popper
                    open={open}
                    anchorEl={anchorRef.current}
                    // role={undefined}
                    transition={true}
                    disablePortal={true}
                >
                    {({ TransitionProps, placement }) => (
                        <Grow {...TransitionProps} style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}>
                            <Paper className={classes.menuPaper}>
                                <ClickAwayListener onClickAway={handleClose}>
                                    <div>
                                        <Box onClick={handleClose} className={classes.menuHeader}>
                                            <Avatar className={classes.headerAvatar}>
                                                {userInitials || <PersonIcon className={classes.userIcon} />}
                                            </Avatar>
                                            <Box className={classes.headerText}>
                                                <Typography className={classes.headerName}>
                                                    {currentUser.data?.appUser?.name}
                                                </Typography>
                                                {firmName && (
                                                    <Typography className={classes.headerFirmName}>
                                                        {firmName}
                                                    </Typography>
                                                )}
                                                <Chip
                                                    variant="outlined"
                                                    size="small"
                                                    label={`Firm ID ${props.tenantId || ''}`}
                                                    className={classes.firmChip}
                                                />
                                                {props.sourceSystem && (
                                                    <Chip
                                                        variant="outlined"
                                                        size="small"
                                                        label={props.sourceSystem}
                                                        className={classes.sourceSystemChip}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                        <Divider className={classes.menuDivider} />
                                        <MenuList autoFocusItem={open} id="menu-list-grow" onKeyDown={handleListKeyDown}>
                                            <ListSubheader disableSticky={true} className={classes.sectionLabel}>Resources</ListSubheader>
                                            <ListItem button={true} onClick={handleResourceOpen} className={clsx(classes.listItem, classes.listItemModern)}>
                                                <ListItemIcon className={classes.listItemIcon}>
                                                    <Box className={classes.iconChip}>
                                                        <InfoIcon className={classes.icon} />
                                                    </Box>
                                                </ListItemIcon>
                                                <ListItemText primary="Resources" />
                                                {resourceOpen ? <ExpandLess /> : <ExpandMore />}
                                            </ListItem>
                                            <Collapse in={resourceOpen} timeout="auto" unmountOnExit={true}>
                                                <List component="div" disablePadding={true}>
                                                    <ListItem button={true} onClick={handleResource(ResourceEnum.Acuity)} className={clsx(classes.listItem, classes.listItemModern, classes.nested)}>
                                                        <ListItemIcon className={classes.listItemIcon}>
                                                            <Box className={classes.iconChip}>
                                                                <ImportContactsIcon className={classes.icon} />
                                                            </Box>
                                                        </ListItemIcon>
                                                        <ListItemText primary="Acuity" />
                                                    </ListItem>
                                                    <ListItem button={true} onClick={handleResource(ResourceEnum.CRM)} className={clsx(classes.listItem, classes.listItemModern, classes.nested)}>
                                                        <ListItemIcon className={classes.listItemIcon}>
                                                            <Box className={classes.iconChip}>
                                                                <ImportContactsIcon className={classes.icon} />
                                                            </Box>
                                                        </ListItemIcon>
                                                        <ListItemText primary="CRM" />
                                                    </ListItem>
                                                    <ListItem button={true} onClick={handleResource(ResourceEnum.Tasks)} className={clsx(classes.listItem, classes.listItemModern, classes.nested)}>
                                                        <ListItemIcon className={classes.listItemIcon}>
                                                            <Box className={classes.iconChip}>
                                                                <ImportContactsIcon className={classes.icon} />
                                                            </Box>
                                                        </ListItemIcon>
                                                        <ListItemText primary="Tasks" />
                                                    </ListItem>
                                                    <ListItem button={true} onClick={handleResource(ResourceEnum.Matters)} className={clsx(classes.listItem, classes.listItemModern, classes.nested)}>
                                                        <ListItemIcon className={classes.listItemIcon}>
                                                            <Box className={classes.iconChip}>
                                                                <ImportContactsIcon className={classes.icon} />
                                                            </Box>
                                                        </ListItemIcon>
                                                        <ListItemText primary="Matters" />
                                                    </ListItem>
                                                    <ListItem button={true} onClick={handleResource(ResourceEnum.Timesheet)} className={clsx(classes.listItem, classes.listItemModern, classes.nested)}>
                                                        <ListItemIcon className={classes.listItemIcon}>
                                                            <Box className={classes.iconChip}>
                                                                <ImportContactsIcon className={classes.icon} />
                                                            </Box>
                                                        </ListItemIcon>
                                                        <ListItemText primary="Timesheet" />
                                                    </ListItem>
                                                </List>
                                            </Collapse>

                                            <ListSubheader disableSticky={true} className={classes.sectionLabel}>Support &amp; Learning</ListSubheader>
                                            <ListItem onClick={handleTrainingWebinars} button={true} className={clsx(classes.listItem, classes.listItemModern)}>
                                                <ListItemIcon className={classes.listItemIcon}>
                                                    <Box className={classes.iconChip}>
                                                        <VideoLibraryIcon className={classes.icon} />
                                                    </Box>
                                                </ListItemIcon>
                                                <ListItemText primary="Training Webinars" />
                                            </ListItem>
                                            <ListItem onClick={handleTrainingUpdates} button={true} className={clsx(classes.listItem, classes.listItemModern)}>
                                                <ListItemIcon className={classes.listItemIcon}>
                                                    <Box className={classes.iconChip}>
                                                        <UpdateIcon className={classes.icon} />
                                                    </Box>
                                                </ListItemIcon>
                                                <ListItemText primary="Feature Updates" />
                                            </ListItem>
                                            <ListItem onClick={handleServiceLevels} button={true} className={clsx(classes.listItem, classes.listItemModern)}>
                                                <ListItemIcon className={classes.listItemIcon}>
                                                    <Box className={classes.iconChip}>
                                                        <TrendingUpIcon className={classes.icon} />
                                                    </Box>
                                                </ListItemIcon>
                                                <ListItemText primary="Service Levels" />
                                            </ListItem>
                                            {UserFeature.HasAccess(UserFeatureEnum.hasReportCatalog) && (
                                                <ListItem onClick={handleReportCatalog} button={true} className={clsx(classes.listItem, classes.listItemModern)}>
                                                    <ListItemIcon className={classes.listItemIcon}>
                                                        <Box className={classes.iconChip}>
                                                            <FormatListBulletedIcon className={classes.icon} />
                                                        </Box>
                                                    </ListItemIcon>
                                                    <ListItemText primary="Reports Catalog" />
                                                </ListItem>
                                            )}
                                            {UserFeature.HasAccess(UserFeatureEnum.hasUserDefinedHelp) && (
                                                <ListItem onClick={handleHelp} button={true} className={clsx(classes.listItem, classes.listItemModern)}>
                                                    <ListItemIcon className={classes.listItemIcon}>
                                                        <Box className={classes.iconChip}>
                                                            <HelpIcon className={classes.icon} />
                                                        </Box>
                                                    </ListItemIcon>
                                                    <ListItemText primary="Help" />
                                                </ListItem>
                                            )}

                                            <ListSubheader disableSticky={true} className={classes.sectionLabel}>Account</ListSubheader>
                                            <ListItem onClick={handleChangePassword} button={true} className={clsx(classes.listItem, classes.listItemModern)}>
                                                <ListItemIcon className={classes.listItemIcon}>
                                                    <Box className={classes.iconChip}>
                                                        <LockIcon className={classes.icon} />
                                                    </Box>
                                                </ListItemIcon>
                                                <ListItemText primary="Change Password" />
                                            </ListItem>
                                            {currentUser.data?.appUser && currentUser.data.appUser.isAdministrator && UserFeature.HasAccess(UserFeatureEnum.hasScrubData) && (
                                                <ListItem onClick={handleScrubData} button={true} className={clsx(classes.listItem, classes.listItemModern)}>
                                                    <ListItemIcon className={classes.listItemIcon}>
                                                        <Box className={classes.iconChip}>
                                                            <StorageIcon className={classes.icon} />
                                                        </Box>
                                                    </ListItemIcon>
                                                    <ListItemText primary="Scrub Enquiry Data" />
                                                </ListItem>
                                            )}
                                            {currentUser.data?.appUser && currentUser.data.appUser.isAdministrator && props.showResetLeapAccess && (
                                                <ListItem onClick={handleResetLeapAccess} button={true} className={clsx(classes.listItem, classes.listItemModern)}>
                                                    <ListItemIcon className={classes.listItemIcon}>
                                                        <Box className={classes.iconChip}>
                                                            <SettingsIcon className={classes.icon} />
                                                        </Box>
                                                    </ListItemIcon>
                                                    <ListItemText primary="Reset LEAP Access" />
                                                </ListItem>
                                            )}
                                            {currentUser.data?.appUser && currentUser.data.appUser.isAdministrator && props.showResetSmokeballAccess && (
                                                <ListItem onClick={handleResetSmokeballAccess} button={true} className={clsx(classes.listItem, classes.listItemModern)}>
                                                    <ListItemIcon className={classes.listItemIcon}>
                                                        <Box className={classes.iconChip}>
                                                            <SettingsIcon className={classes.icon} />
                                                        </Box>
                                                    </ListItemIcon>
                                                    <ListItemText primary="Reset Smokeball Access" />
                                                </ListItem>
                                            )}
                                            {currentUser.data?.appUser && currentUser.data.appUser.isAdministrator && props.showResetActionStepAccess && (
                                                <ListItem onClick={handleResetActionStepAccess} button={true} className={clsx(classes.listItem, classes.listItemModern)}>
                                                    <ListItemIcon className={classes.listItemIcon}>
                                                        <Box className={classes.iconChip}>
                                                            <SettingsIcon className={classes.icon} />
                                                        </Box>
                                                    </ListItemIcon>
                                                    <ListItemText primary="Reset ActionStep Access" />
                                                </ListItem>
                                            )}
                                            {currentUser.data?.appUser && currentUser.data.appUser.isAdministrator && props.showResetAffinityAccess && (
                                                <ListItem onClick={handleResetAffinityAccess} button={true} className={clsx(classes.listItem, classes.listItemModern)}>
                                                    <ListItemIcon className={classes.listItemIcon}>
                                                        <Box className={classes.iconChip}>
                                                            <SettingsIcon className={classes.icon} />
                                                        </Box>
                                                    </ListItemIcon>
                                                    <ListItemText primary="Reset Affinity Access" />
                                                </ListItem>
                                            )}

                                            <Divider className={classes.menuDivider} />
                                            <ListItem onClick={handleLogout} button={true} className={clsx(classes.listItem, classes.listItemModern, classes.logoutItem)}>
                                                <ListItemIcon className={classes.listItemIcon}>
                                                    <Box className={classes.iconChip}>
                                                        <ExitToAppIcon className={classes.icon} />
                                                    </Box>
                                                </ListItemIcon>
                                                <ListItemText primary="Logout" />
                                            </ListItem>
                                        </MenuList>
                                    </div>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
            </Box>
        </>
    );
};
